import { Theme, ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import ToastError from '@/assets/icons/alert-info.svg?react';
import ToastSuccess from '@/assets/icons/toast-success.svg?react';
import { ColorSchemeValueContext } from '@/context/colorSchemeContext';
import createDashboardTheme from '@/styles/theme';

interface Props {
  readonly text: string;
  readonly type: 'success' | 'error';
}

export const Toast: React.FC<Props> = (props: Props): React.ReactElement | null => {
  const { text, type } = props;

  const colorScheme = React.useContext<'light' | 'dark'>(ColorSchemeValueContext);
  const theme = React.useMemo((): Theme => createDashboardTheme(colorScheme), [colorScheme]);

  if (text === '') {
    return <></>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper elevation={5} sx={[styles.paper, styles[type]]}>
        <Box display="flex" alignItems="center" gap={1} p={3}>
          {type === 'success' ? (
            <ToastSuccess color={theme.palette.primary.main} fill="currentColor" />
          ) : (
            <ToastError fill="currentColor" />
          )}
          <Typography variant="body1">{text}</Typography>
        </Box>
      </Paper>
    </ThemeProvider>
  );
};

const styles: Record<string, SystemStyleObject<Theme>> = {
  paper: {
    position: 'fixed',
    top: '2rem',
    right: '1.5rem',
    borderRadius: 1,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
  success: {
    backgroundColor: 'custom.Primary1Background',
  },
  error: {
    backgroundColor: 'error.light',
    color: 'error.main',
  },
};
